/**
 * Composer placeholder text for builder mode.
 *
 * Shared by the two builder composers — `BuilderChat` (the pre-conversation
 * `/build` route) and `ReasoningChat` (the follow-up composer once a
 * conversation exists) — because both ask the user for the same thing and must
 * not word it differently. Extracted for the same reason as
 * `builderRowLabel.ts`: a second surface needed the string, and one shared
 * module is cheaper than two that drift.
 *
 * Both previous strings were wrong in their own way. `BuilderChat` hardcoded
 * "Describe what you want this automation to do" — an instruction rather than
 * an example (TONE_STYLE_GUIDE § Placeholder Text lists that exact shape under
 * "Don't"), and it says "automation" on a route that also builds validation
 * rules. `ReasoningChat` fell back to the generic "What can I help with
 * today?", which is not builder-aware at all.
 */

import { getEntityTypeLabel } from '@/components/csv-import/utils/entityTypeLabels';
import {
  AUTOMATION_ENTITY_TYPE,
  VALIDATION_RULE_ENTITY_TYPE,
} from '@/lib/reasoning/entityTypes';

/**
 * Examples, not instructions — the user should read the placeholder and learn
 * what is possible here, not be told to type.
 *
 * The vocabulary is drawn from what these records actually hold, so the
 * examples describe real capability rather than plausible-sounding fiction: an
 * event automation triggers on a CRM event against an entity type
 * (`ahoy_triggering_event_types` / `ahoy_triggering_entity_types`), and a
 * validation rule pairs a condition and a constraint with a message shown to
 * the user (`ahoy_condition` / `ahoy_constraint` / `ahoy_message`).
 */
const PLACEHOLDERS = {
  [AUTOMATION_ENTITY_TYPE]: {
    create: 'When a deal moves to Won, notify the account owner',
    refine: 'Only run this for enterprise deals, change who gets notified',
  },
  [VALIDATION_RULE_ENTITY_TYPE]: {
    create: 'Require a close date before a deal can move to Won',
    refine: 'Only apply this to new deals, reword the message',
  },
} as const;

/**
 * Used when there is no entity type at all — the pre-conversation route before
 * a type is chosen, or a builder conversation whose subject has not resolved.
 *
 * There is nothing to name here, so this is the one genuinely generic case.
 */
const UNTYPED = {
  create: 'Describe what you want to create',
  refine: 'Ask for a change',
} as const;

/**
 * Used for a type we have no hand-written copy for.
 *
 * These ARE instructions rather than examples, which the tone guide otherwise
 * warns against — deliberately, and for a reason that will not go away: writing
 * an example requires knowing the domain, and this branch exists precisely for
 * the types we do not know. What the guide actually objects to is vagueness
 * ("Ask me anything..."); naming the record keeps it concrete, which is the
 * best available answer for an arbitrary type.
 *
 * This branch matters more than it looks. `supportsBuilderMode` is a per-type
 * capability, not an automation-kind classification
 * (`entityTypeRegistry.ts:428-433`), so as the builder reaches more record
 * types this stops being the edge case and becomes the common one. Add
 * hand-written examples above whenever we know the domain well enough.
 */
function forNamedType(label: string, mode: 'create' | 'refine'): string {
  // Lowercased because the label is Title Case ("Workspace User") and these
  // read as sentences, which the tone guide sets in sentence case.
  const noun = label.toLowerCase();
  return mode === 'create'
    ? `Describe the ${noun} you want to create`
    : `Ask for a change to this ${noun}`;
}

export interface BuilderPlaceholderArgs {
  /**
   * The record type being built. Null/undefined on the pre-conversation route
   * before a type is chosen, and on a conversation whose subject has not
   * resolved yet.
   */
  entityTypeName: string | null | undefined;
  /**
   * Whether something already exists on the panel — an existing record the
   * user came in to edit, or a draft the agent has proposed.
   *
   * This deliberately does NOT distinguish "arrived here to edit" from "created
   * it a moment ago and it has since saved". Both mean the same thing to the
   * person typing: there is a thing beside the chat, so the next message
   * refines it rather than describes it from nothing. The wording therefore
   * flips mid-conversation on the create path, once the first proposal forms —
   * which is correct, not a bug to design around.
   */
  hasSubject: boolean;
}

/**
 * Placeholder for a builder composer, varying by record type and by whether
 * there is anything to refine yet.
 */
export function getBuilderPlaceholder({
  entityTypeName,
  hasSubject,
}: BuilderPlaceholderArgs): string {
  const mode = hasSubject ? 'refine' : 'create';
  if (!entityTypeName) {
    return UNTYPED[mode];
  }
  const handWritten = PLACEHOLDERS[entityTypeName as keyof typeof PLACEHOLDERS];
  if (handWritten) {
    return handWritten[mode];
  }
  // getEntityTypeLabel resolves the built-in registry and falls back to
  // snake_case -> Title Case, so a custom `org_workspace_user` still names
  // itself as "Workspace User" rather than degrading to generic copy.
  return forNamedType(getEntityTypeLabel(entityTypeName), mode);
}
